import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { post } from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { MDBCol, MDBRow } from 'mdbreact';

import { actionSvgs } from 'assets/icons';

import useFetch from 'hooks/useFetch';
import endpoints from 'services/endpoints';

import FilledButton from 'components/Buttons/FilledButton';
import UnfilledButton from 'components/Buttons/UnfilledButton';

import FeaturedText from 'components/FeaturedText';
import Footer from 'components/Footer';
import Overlay from 'components/Informations/Overlay';
import ReturnPreviousPage from 'components/ReturnPreviousPage';
import Toast from 'components/Informations/Toast';

import DateField from 'components/FormGroup/DateField';
import ErrorMessage from 'components/FormGroup/ErrorMessage';
import InputField from 'components/FormGroup/InputField';
import SelectField from 'components/FormGroup/SelectField';

import { formatDate, totallyHours } from 'utils/moment';
import { salesForceInputs } from 'utils/masks';

import ContentFormCard from 'components/Cards/ContentFormCard';
import Accordion from 'components/Accordion';

import * as S from './styles';

const { getSales, salesFunctionTypes, insertSale, deleteSale } = endpoints;

const initialValues = {
  saleDate: new Date(),
  saleType: '',
  additiveNumber: '',
  quantityHours: '',
  allocatedFunction: '',
  seniorLevel: '',
};

const validationSchema = Yup.object().shape({
  saleDate: Yup.string().required('Insira a data da venda'),
  saleType: Yup.string().required('Insira o tipo da venda'),
  additiveNumber: Yup.string()
    .min(9, 'Número de identificação incorreto')
    .max(9, 'Número de identificação incorreto'),
  quantityHours: Yup.string().required(
    'Insira a quantidade de horas trabalhadas',
  ),
  allocatedFunction: Yup.string().required('Selecione a função do alocado'),
  seniorLevel: Yup.string().required('Selecione o nível de senioridade'),
});

function AddSales({ location, history, user }) {
  const { state } = location;
  const { id_funcionario: idFuncionario, administrador } = user;
  const isAdmin = administrador === 1;

  const [overlayVisibility, setOverlayVisibility] = useState(false);
  const [overlayType, setOverlayType] = useState('');
  const [overlayAction, setOverlayAction] = useState(() => {});

  const [toastVisibility, setToastVisibility] = useState(false);
  const [toastType, setToastType] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastClose, setToastClose] = useState(2000);

  const [formAlterations, setFormAlterations] = useState(false);
  const [salesInProject, setSalesInProject] = useState([]);
  const [selectOptions, setSelectOptions] = useState({});
  const [salesApi, reloadSalesApi] = useState(false);
  const [saleRmId, setSaleRmId] = useState();
  const [accordionOpen, setAccordionOpen] = useState(true);

  const [salesList] = useFetch(getSales, salesApi);
  const [salesFunctionsOptions] = useFetch(salesFunctionTypes);

  useEffect(() => {
    if (!state) return history.goBack();

    (async () => {
      const sales = salesList;
      const options = salesFunctionsOptions;
      const { idProjeto } = await state;
      const { Funcao, Nivel, TipoVenda } = options;
      const salesFilter = sales
        ? sales.filter(sale => sale.id_projeto === idProjeto)
        : [];
      const orderedSales = salesFilter.sort(
        (a, b) => new Date(b.dt_venda) - new Date(a.dt_venda),
      );

      if (Object.keys(options).length) {
        const functionsOpts = await Funcao.map(item => ({
          value: item.id_funcao,
          label: item.nome_funcao,
        }));

        const levelOpts = await Nivel.map(item => ({
          value: item.id_nivel,
          label: item.senioridade,
        }));

        const salesOpts = await TipoVenda.map(item => ({
          value: item.id_tipo_venda,
          label: item.tipo_venda,
        }));

        setSelectOptions(prev => ({
          ...prev,
          functionsOpts,
          levelOpts,
          salesOpts,
        }));
      }
      setSalesInProject(orderedSales);
    })();

    const unMount = () => {
      setSalesInProject([]);
      setSelectOptions({});
    };

    return unMount;
  }, [salesList, salesFunctionsOptions, history, state]);

  const renderToast = useCallback(
    (type, message, closeTime) => {
      const condition = closeTime !== undefined ? closeTime : toastClose;

      setToastVisibility(true);
      setToastMessage(message);
      setToastType(type);
      setToastClose(condition);

      setTimeout(() => {
        setToastVisibility(false);
        setToastMessage('');
        setToastType('');
      }, condition + 1500);
    },
    [toastClose],
  );

  const renderOverlay = useCallback(
    type => {
      const response = {
        addSale: () => (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>adicionando</span>,
              ' uma nova venda e este processo não poderá ser ',
              <span>revertido</span>,
              ' ou ',
              <span>editado</span>,
              '. Deseja prosseguir com a adição?',
            ]}
          >
            <UnfilledButton
              text="Sim, prosseguir"
              handleClick={() => {
                overlayAction();
                setOverlayType('');
                setOverlayVisibility(false);
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayType('');
                setOverlayVisibility(false);
              }}
            />
          </Overlay>
        ),
        rmSale: () => (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Vocẽ está prestes a ',
              <span>remover</span>,
              ' esta venda.',
              <br />,
              'Deseja prosseguir?',
            ]}
          >
            <UnfilledButton
              text="Sim, prosseguir"
              handleClick={() => {
                const config = {
                  headers: {
                    'Content-Type': 'Application/JSON',
                  },
                };

                const obj = {
                  id_user_up: idFuncionario,
                  id_venda: saleRmId,
                };

                post(deleteSale, obj, config)
                  .then(() => {
                    reloadSalesApi(prev => !prev);
                    setOverlayVisibility(false);
                    setOverlayType();
                    renderToast('success', 'Venda removida com sucesso', 6000);
                  })
                  .catch(() => {
                    setSaleRmId();
                    setOverlayVisibility(false);
                    setOverlayType();
                    renderToast(
                      'error',
                      'Occoreu algum erro ao tentar remover esta venda. Tente novamente ou verifique com o nosso suporte.',
                      6000,
                    );
                  });
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayVisibility(false);
                setOverlayType();
              }}
            />
          </Overlay>
        ),
        cancel: () => (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>cancelando</span>,
              ' a inserção de uma nova venda e esta ação irá ',
              <span>descartar</span>,
              ' a venda não inserida. Deseja prosseguir?',
            ]}
          >
            <UnfilledButton
              text="Sim, prosseguir"
              handleClick={() => {
                setOverlayVisibility(false);
                setOverlayType('');
                history.goBack();
              }}
            />
            <FilledButton
              text="Não"
              buttonClass="warn"
              handleClick={() => {
                setOverlayVisibility(!overlayVisibility);
                setOverlayType('');
              }}
            />
          </Overlay>
        ),
        returning: () => (
          <Overlay
            isOpen={overlayVisibility}
            type="warning"
            text={[
              'Você está ',
              <span>voltando para a tela anterior</span>,
              ' e esta ação irá ',
              <span>descartar</span>,
              ' as vendas que não foram salvas. Deseja continuar?',
            ]}
          >
            <UnfilledButton
              text="Sim, continuar"
              handleClick={() => history.goBack()}
            />
            <FilledButton
              text="Não, permanecer"
              buttonClass="warn"
              handleClick={() => setOverlayVisibility(!overlayVisibility)}
            />
          </Overlay>
        ),
      };
      const overlay = response[type];
      if (type) {
        return overlay();
      }
    },
    [
      history,
      idFuncionario,
      overlayAction,
      overlayVisibility,
      renderToast,
      saleRmId,
    ],
  );

  useEffect(() => {
    (async () => {
      const response = await saleRmId;
      if (response) {
        renderOverlay('rmSale');
      }
    })();
  }, [saleRmId, renderOverlay]);

  const submitForm = data => {
    const {
      saleType,
      saleDate,
      additiveNumber,
      quantityHours,
      allocatedFunction,
      seniorLevel,
    } = data;

    const { idProjeto, idCliente, idAccManager } = location.state;

    const obj = {
      id_usr_in: idFuncionario,
      id_usr_up: idFuncionario,
      num_horas: quantityHours * 3600,
      dt_venda: formatDate(saleDate, 'YYYY-MM-DD 00:00:00'),
      account_manager: idAccManager || null,
      id_tipo_venda: saleType,
      id_cliente: idCliente,
      id_projeto: idProjeto,
      id_funcao: allocatedFunction,
      id_nivel: seniorLevel,
      num_aditivo: additiveNumber,
    };
    const config = {
      headers: {
        'Content-Type': 'Application/JSON',
      },
    };

    post(insertSale, obj, config)
      .then(() => {
        renderToast('success', 'Venda adicionada com sucesso', 5000);
        reloadSalesApi(prev => !prev);
      })
      .then(setFormAlterations(false))
      .catch(() =>
        renderToast(
          'error',
          'Falha ao adicionar venda. Por favor tente novamente',
        ),
      );
  };

  const setIdSaleToRm = async id => {
    const response = await id;
    setSaleRmId(response);
    setOverlayVisibility(true);
    setOverlayType('rmSale');
  };

  return (
    !!Object.keys(selectOptions).length && (
      <>
        {overlayVisibility && renderOverlay(overlayType)}

        {toastVisibility && (
          <Toast
            type={toastType}
            message={toastMessage}
            autoClose={toastClose}
            hideProgress
          />
        )}

        <S.Container>
          <MDBRow>
            <MDBCol lg="12">
              <ReturnPreviousPage
                text="Voltar para gerência de projetos"
                handleClick={() => {
                  const haveAlterations = () => {
                    setOverlayVisibility(true);
                    setOverlayType('returning');
                  };

                  const dontHaveAlterations = () => {
                    setToastVisibility(false);
                    history.goBack();
                  };

                  return formAlterations
                    ? haveAlterations()
                    : dontHaveAlterations();
                }}
              />
              <S.Introduction>
                <S.FeaturedText>
                  {state.nome}
                  <span>({state.identificacao})</span>
                </S.FeaturedText>
                <FeaturedText text="Cadastre novas vendas ao projeto selecionado" />
              </S.Introduction>
            </MDBCol>
          </MDBRow>
          {isAdmin && (
            <MDBRow>
              <MDBCol lg={6} sm={12}>
                <S.Divider>
                  Aqui você encontra as informações gerais do projeto
                </S.Divider>
                <Formik
                  initialValues={initialValues}
                  enableReinitialize
                  validateOnBlur={false}
                  validateOnChange={false}
                  validationSchema={validationSchema}
                  onSubmit={e => submitForm(e)}
                >
                  {({
                    values,
                    errors,
                    handleSubmit,
                    setFieldValue,
                    isSubmitting,
                  }) => (
                    <S.Form onSubmit={e => submitForm(e, values)}>
                      <DateField
                        handleChange={e => {
                          setFieldValue('saleDate', e);
                          setFormAlterations(true);
                        }}
                        selectedValue={values.saleDate || new Date()}
                        placeholderText="Insira a data"
                        onlyMonth={false}
                        id="saleDate"
                        name="saleDate"
                        labelText="Data da venda (*)"
                      />
                      {!!errors.saleDate && (
                        <ErrorMessage message={errors.saleDate} />
                      )}

                      {!!Object.keys(selectOptions).length && (
                        <SelectField
                          options={selectOptions.salesOpts}
                          value={selectOptions.salesOpts.find(
                            option =>
                              values && option.value === values.saleType,
                          )}
                          handleChange={({ value }) => {
                            setFieldValue('saleType', value);
                            setFormAlterations(true);
                          }}
                          hasLabelMargin
                          labelText="Tipo da venda (*)"
                          placeholderText="Selecione o tipo da venda"
                          name="saleType"
                        />
                      )}
                      {!!errors && !!errors.saleType && (
                        <ErrorMessage message={errors.saleType} />
                      )}

                      <InputField
                        labelText="Número do aditivo"
                        placeholderText="Insira o número do aditivo"
                        value={salesForceInputs(values.additiveNumber)}
                        name="additiveNumber"
                        handleChange={e => {
                          setFieldValue(
                            'additiveNumber',
                            salesForceInputs(e.target.value),
                          );
                          setFormAlterations(true);
                        }}
                      />
                      {!!errors.additiveNumber && (
                        <ErrorMessage message={errors.additiveNumber} />
                      )}

                      <InputField
                        labelText="Quantidade de horas (*)"
                        placeholderText="Horas trabalhadas nesta venda"
                        value={
                          values.quantityHours &&
                          values.quantityHours.replace(
                            /[\W\s]|[a-z|A-Z]*$/gm,
                            '',
                          )
                        }
                        name="quantityHours"
                        handleChange={e => {
                          setFieldValue('quantityHours', e.target.value);
                          setFormAlterations(true);
                        }}
                        pattern="^[\d]*$"
                      />
                      {!!errors.quantityHours && (
                        <ErrorMessage message={errors.quantityHours} />
                      )}

                      {!!Object.keys(selectOptions).length && (
                        <SelectField
                          options={selectOptions.functionsOpts}
                          value={selectOptions.functionsOpts.find(
                            option => option.value === values.allocatedFunction,
                          )}
                          handleChange={({ value }) => {
                            setFieldValue('allocatedFunction', value);
                            setFormAlterations(true);
                          }}
                          hasLabelMargin
                          labelText="Função do alocado (*)"
                          placeholderText="Selecione o responsável técnico"
                          name="allocatedFunction"
                        />
                      )}
                      {!!errors.allocatedFunction && (
                        <ErrorMessage message={errors.allocatedFunction} />
                      )}

                      {!!Object.keys(selectOptions).length && (
                        <SelectField
                          options={selectOptions.levelOpts}
                          value={selectOptions.levelOpts.find(
                            option => option.value === values.seniorLevel,
                          )}
                          handleChange={({ value }) => {
                            setFieldValue('seniorLevel', value);
                            setFormAlterations(true);
                          }}
                          hasLabelMargin
                          labelText="Nível de senioridade (*)"
                          placeholderText="Selecione um nível"
                          name="seniorLevel"
                        />
                      )}
                      {!!errors.seniorLevel && (
                        <ErrorMessage message={errors.seniorLevel} />
                      )}

                      <S.Buttons>
                        <FilledButton
                          buttonClass="cancel"
                          text="Cancelar"
                          handleClick={() => {
                            setOverlayVisibility(true);
                            setOverlayType('cancel');
                          }}
                        />
                        <FilledButton
                          buttonClass="confirm"
                          text="Cadastrar venda"
                          disabled={!formAlterations || isSubmitting}
                          handleClick={() => {
                            setOverlayVisibility(true);
                            setOverlayType('addSale');
                            setOverlayAction(() => handleSubmit);
                          }}
                        />
                      </S.Buttons>
                    </S.Form>
                  )}
                </Formik>
              </MDBCol>
            </MDBRow>
          )}
          <MDBRow>
            <MDBCol lg="6" sm="12">
              <S.Divider>
                Estas são as vendas cadastradas neste projeto
              </S.Divider>
              <Accordion
                accordionOpen={accordionOpen}
                setAccordionOpen={setAccordionOpen}
                headText="Vendas inseridas"
                callbackText="Não existem vendas cadastradas neste projeto"
              >
                {salesInProject.length &&
                  salesInProject.map(sale => (
                    <ContentFormCard key={sale.id_venda_hora}>
                      <div>
                        <p>
                          {sale.dt_venda.split('-')[0] === '0000' ? (
                            <span> Sem data de venda </span>
                          ) : (
                            formatDate(sale.dt_venda)
                          )}
                        </p>

                        <p>
                          {sale.nome_funcao}
                          {sale.senioridade && (
                            <small>({sale.senioridade})</small>
                          )}
                        </p>

                        <p>
                          <span>Tipo de venda:</span>
                          {sale.tipo_venda}
                        </p>

                        <p>
                          <span>Quantidade de horas:</span>
                          {`${totallyHours(sale.num_horas).split(':')[0]}h`}
                        </p>
                      </div>

                      {isAdmin && (
                        <S.RemoveSale
                          onClick={async () =>
                            await setIdSaleToRm(sale.id_venda_hora)
                          }
                        >
                          {actionSvgs('remove')}
                        </S.RemoveSale>
                      )}
                    </ContentFormCard>
                  ))}
              </Accordion>
            </MDBCol>
          </MDBRow>
        </S.Container>
        <Footer />
      </>
    )
  );
}

export default AddSales;

AddSales.defaultProps = {
  history: {},
  location: {},
  user: {},
};

AddSales.propTypes = {
  history: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  ),
  location: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  user: PropTypes.shape(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  ),
};
